import type { CSSProperties, ReactNode } from 'react'
import s from './LineList.module.scss'

/**
 * Eine Liste aus einzeiligen Einträgen.
 *
 * Drei Teile je Zeile: links eine schmale Spalte für Datum oder Art, in der
 * Mitte der dehnbare Text, rechts ein grauer Zusatz. Fällige Aufgaben,
 * erwartete Termine und Belege haben genau diese Form.
 *
 * Keine Tabelle: Die brächte Kopfzeile, Sortierung und Blättern mit — für eine
 * Nebenliste unter der eigentlichen Tabelle wäre das zu schwer. Und kein
 * `FieldGrid`: Das ist Beschriftung → Wert, hier sind es drei gleichrangige
 * Teile.
 */
export function LineList({ children }: { children: ReactNode }) {
  return <div className={s.list}>{children}</div>
}

interface ItemProps {
  /** Links, schmal und in Monospace — Datum, Rhythmus oder Art. */
  lead: ReactNode
  /** Hebt die linke Spalte hervor: eine dringende Aufgabe, ein unsicherer Termin. */
  leadTone?: 'neutral' | 'warn' | 'bad'
  /** Breite der linken Spalte. Untereinander stehende Listen wirken sonst unruhig. */
  leadWidth?: string
  /** Rechts, grau — der Zusammenhang: zu welchem Fall, welcher Titel. */
  aside?: ReactNode
  /** Rechts außen, hervorgehoben — ein Vorbehalt wie „nicht bestätigt". */
  flag?: ReactNode
  /**
   * Der vollständige Text, für den Fall dass er gekürzt wird.
   *
   * Die mittlere Spalte kürzt mit „…“, sobald der Platz nicht reicht. Ohne
   * diese Angabe wäre der gekürzte Text wertlos: „Verkündungstermin Berufun…“
   * und „Verkündungstermin Beruhig…“ ließen sich nicht unterscheiden.
   */
  hint?: string
  children: ReactNode
}

export function LineItem({ lead, leadTone = 'neutral', leadWidth = '96px',
                          aside, flag, hint, children }: ItemProps) {
  return (
    <div className={s.item}>
      {/*
        Die Breite geht als CSS-Variable und nicht als `flex-basis` hinein:
        Auf dem Handy setzt das Stylesheet sie außer Kraft — 150 px für das
        Datum ließen dem eigentlichen Text bei 375 px keine 150 mehr.
      */}
      <span className={s.lead} data-tone={leadTone}
            style={{ '--lead-width': leadWidth } as CSSProperties}>
        {lead}
      </span>
      <span className={s.text} title={hint}>{children}</span>
      {aside && <span className={s.aside}>{aside}</span>}
      {flag && <span className={s.flag}>{flag}</span>}
    </div>
  )
}
